import {gql} from '@apollo/client';
import EditIcon from '@material-ui/icons/Edit';
import HistoryIcon from '@material-ui/icons/History';
import {Pane, TickCircleIcon} from 'evergreen-ui';
import React, {ReactNode, useEffect, useReducer} from 'react';
import {useCurrentUser} from 'security';
import {
    Column,
    EmailLabelWithCopy,
    EnrichedTable,
    ErrorDisplay,
    LoadingSpinner,
    RelayConnection,
    SimpleDropdown,
    SortBy,
} from '../../../common';
import {formatISODateStr} from '../../../common/util/date';
import {useLazyQuery$} from '../../../graphqlsupport';
import {SortOrder} from '../../../placement/domains';
import {useObservable} from '../../../rxsupport';
import {hasContactEditAccess} from '../../domain/security';
import {
    RelayPagination,
    RelayPaginationActionPayload,
    RelayPaginationState,
    reduceRelayPaginationAction,
    useRelayPagination,
} from '../../pagination';
import {BusinessContactFilter} from './BusinessContactFilter';
import {BusinessContactFilterBadge} from './BusinessContactFilterBadge';
import {asQueryParam, BusinessContactFilterValue, EMPTY_FILTER, isNarrowed} from './domain/filter';
import {BusinessContactRow} from './domain/types';
import {formatUsPhone} from './domain/phone';
import {useBusinessFunctionOptions} from './useBusinessFunctionOptions';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = ['10', '25', '50', '100'];
const STATUS_GREEN = '#52BD94';
/** A field Salesforce left empty is a fact about the contact - never a blank cell. */
const EMPTY_CELL = '-';

// FE column attr -> BE whitelist sort key (BusinessContactQueryAdapter#resolveSort). A column absent
// from this map is not sortable: the map is the guard.
export const SORT_KEY_BY_COLUMN: Partial<Record<keyof BusinessContactRow, string>> = {
    firstName: 'contactName',
    title: 'title',
    businessFunction: 'businessFunction',
    contactPhone: 'contactPhone',
    inactive: 'status',
    sfLastModifiedDate: 'lastUpdated',
    notesForCcs: 'notesForCcs',
};
const DEFAULT_SORT_KEY = 'contactName';
const DEFAULT_SORT: SortBy<BusinessContactRow> = {column: 'firstName', order: SortOrder.ASC};

export const BUSINESS_CONTACTS_QUERY = gql`
    query businessContacts($first: Int = 10, $after: String, $sortBy: String, $sortDir: String,
                           $search: String, $businessFunctions: [String], $status: String) {
        businessContacts(first: $first, after: $after, sortBy: $sortBy, sortDir: $sortDir,
                         search: $search, businessFunctions: $businessFunctions, status: $status) {
            edges {
                cursor
                node {
                    guid
                    providerGuid
                    sfId
                    firstName
                    lastName
                    suffix
                    email
                    title
                    businessFunction
                    contactPhone
                    notesForCcs
                    inactive
                    inactiveReason
                    sfLastModifiedDate
                }
            }
            totalElements
            pageInfo {
                hasPreviousPage
                endCursor
                hasNextPage
                startCursor
            }
        }
    }
`;

type BusinessContactsResponse = RelayConnection<BusinessContactRow> & {totalElements: number};

type BusinessContactState = {
    pagination: RelayPaginationState;
    pageSize: number;
    sortBy: SortBy<BusinessContactRow>;
    /** The funnel's applied values, search included. Kept in the table only, so leaving the page clears them. */
    filter: BusinessContactFilterValue;
};
type BusinessContactAction =
    | RelayPaginationActionPayload
    | {type: 'SET_PAGE_SIZE'; pageSize: number}
    | {type: 'SET_SORT_BY'; sortBy: SortBy<BusinessContactRow>}
    | {type: 'SET_FILTER'; filter: BusinessContactFilterValue};

export function businessContactReducer(
    state: BusinessContactState,
    action: BusinessContactAction,
): BusinessContactState {
    switch (action.type) {
        case 'RELAY_PAGINATION_ACTION':
            return {...state, pagination: reduceRelayPaginationAction(state.pagination, action)};
        case 'SET_PAGE_SIZE':
            // Page-size change resets to the first page.
            return {...state, pageSize: action.pageSize, pagination: new RelayPaginationState('0', action.pageSize)};
        case 'SET_SORT_BY':
            // Sort change resets to the first page.
            return {...state, sortBy: action.sortBy, pagination: new RelayPaginationState('0', state.pageSize)};
        case 'SET_FILTER':
            // A narrower list may not reach the page the user was on, so it starts at the first.
            return {...state, filter: action.filter, pagination: new RelayPaginationState('0', state.pageSize)};
        default:
            return state;
    }
}

const isBlank = (value: string | null | undefined): boolean => (value ?? '').trim() === '';

const orDash = (value: string | null | undefined): string => (isBlank(value) ? EMPTY_CELL : value as string);

/** First name, last name and a suffix when the contact carries one, with the blanks dropped. */
export const fullName = (row: BusinessContactRow): string =>
    [row.firstName, row.lastName, row.suffix].filter((part) => !isBlank(part)).join(' ');

/** The row's pencil. Mirrors user management's EditUserButton, which is the house shape for this. */
const EditContactButton = ({onClick, contactName}: {onClick: () => void; contactName: string}) => (
    <button type="button"
            aria-label={`Edit ${contactName}`}
            className="border-atlantis-200 hover:bg-gray-200 bg-transparent text-atlantis-200 outline-none
                       focus:outline-none h-8 w-8 text-center font-extrabold flex items-center justify-center text-xl
                       rounded-full border"
            onClick={onClick}>
        <EditIcon fontSize="inherit"/>
    </button>
);

/** The row's clock, the pencil's twin. An inactive contact has a history too, so every row carries one. */
const HistoryContactButton = ({onClick, contactName}: {onClick: () => void; contactName: string}) => (
    <button type="button"
            aria-label={`History for ${contactName}`}
            className="border-atlantis-200 hover:bg-gray-200 bg-transparent text-atlantis-200 outline-none
                       focus:outline-none h-8 w-8 text-center font-extrabold flex items-center justify-center text-xl
                       rounded-full border"
            onClick={onClick}>
        <HistoryIcon fontSize="inherit"/>
    </button>
);

/** One word either way, so it holds its line however narrow the drawer makes the column. */
const StatusCell = ({row}: {row: BusinessContactRow}) => {
    if (row.inactive) {
        return <span className="whitespace-no-wrap">Inactive</span>;
    }
    return (
        <span className="inline-flex items-center whitespace-no-wrap" style={{color: STATUS_GREEN}}>
            <TickCircleIcon size={14} color={STATUS_GREEN} marginRight={6}/>
            Active
        </span>
    );
};

type Props = {
    /** Opens the drawer on that contact. The page owns the drawer, so the pencil only reports. */
    onEdit: (row: BusinessContactRow) => void;
    /** Opens the drawer on that contact's history. The clock only reports, like the pencil. */
    onHistory: (row: BusinessContactRow) => void;
    /**
     * Bumped by the page after a save. The table's query is private to this component, and its rows
     * key on guid rather than id, so a mutation result never lands on the cached connection and
     * Apollo cannot refresh the grid on its own. This is the page's handle on the refetch.
     */
    refreshToken: number;
    /** Right side of the toolbar, before the pagination controls: the Add Contact button. */
    rightActions?: ReactNode;
};

export const BusinessContactTable = ({onEdit, onHistory, refreshToken, rightActions}: Props) => {
    const canEdit = hasContactEditAccess(useCurrentUser());
    const [{pagination, pageSize, sortBy, filter}, dispatch] = useReducer(businessContactReducer, {
        pagination: new RelayPaginationState('0', DEFAULT_PAGE_SIZE),
        pageSize: DEFAULT_PAGE_SIZE,
        sortBy: DEFAULT_SORT,
        filter: EMPTY_FILTER,
    });
    // One read of the picklist serves both the funnel and the chips. Called before the grid's own
    // effect, so the grid's query is always the last one this component runs.
    const {options: functionOptions} = useBusinessFunctionOptions();

    const queryVars = () => ({
        ...pagination.toGraphQlParams(),
        sortBy: SORT_KEY_BY_COLUMN[sortBy.column as keyof BusinessContactRow] ?? DEFAULT_SORT_KEY,
        sortDir: sortBy.order,
        ...asQueryParam(filter),
    });

    const {execute, data, isRunning$, error} = useLazyQuery$<
        ReturnType<typeof queryVars>,
        {businessContacts: BusinessContactsResponse}
    >(BUSINESS_CONTACTS_QUERY, queryVars());

    useEffect(() => {
        execute(queryVars());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination, sortBy, filter, refreshToken]);

    const loading = useObservable(isRunning$, true);
    const totalRows = data?.businessContacts?.totalElements ?? 0;
    const {onPaginationClick} = useRelayPagination(dispatch, totalRows);
    const rows = data?.businessContacts?.edges.map((edge) => edge.node) ?? [];

    if (error) {
        return <ErrorDisplay error={error} title="Error loading business contacts"/>;
    }

    return (
        <Pane className="flex flex-col" flexGrow={1}>
            <Pane display="flex" justifyContent="flex-end" alignItems="center" marginBottom={16}>
                <BusinessContactFilter initFilterValue={filter}
                                       options={functionOptions}
                                       onApply={(applied) => dispatch({type: 'SET_FILTER', filter: applied})}/>
                {rightActions}
                <SimpleDropdown options={PAGE_SIZE_OPTIONS} selected={pageSize.toString()}
                                minWidth={'2rem'}
                                textColor={'gray-700'}
                                setSelected={(newPageSize) => dispatch({type: 'SET_PAGE_SIZE', pageSize: parseInt(newPageSize)})}/>
                <RelayPagination pageInfo={data?.businessContacts.pageInfo}
                                 count={totalRows}
                                 onClick={onPaginationClick}/>
            </Pane>
            <BusinessContactFilterBadge filter={filter}
                                        options={functionOptions}
                                        onChange={(changed) => dispatch({type: 'SET_FILTER', filter: changed})}/>
            <Pane borderTop="1px solid #E6E8EC" marginBottom={20}/>
            <EnrichedTable<BusinessContactRow>
                firstColumn={'firstName'}
                sortBy={sortBy}
                setSortBy={(col: Column<BusinessContactRow>) => {
                    if (!SORT_KEY_BY_COLUMN[col.attr as keyof BusinessContactRow]) {
                        return;
                    }
                    // First click on a column sorts desc; clicking the active desc column flips to asc.
                    const order = col.attr === sortBy.column && sortBy.order === SortOrder.DESC
                        ? SortOrder.ASC
                        : SortOrder.DESC;
                    dispatch({type: 'SET_SORT_BY', sortBy: {column: col.attr, order}});
                }}
                styleOverride={{
                    tableContent: 'overflow-auto flex flex-col flex-grow mb-4 scrollbar-always portal-wrap-cells',
                    trHeader: 'portal-history-header text-left select-none text-sm',
                }}
                // Spans are sized so a phone number and a timestamp hold one line at full width
                // and wrap, rather than spill, once the drawer narrows the table.
                columns={[
                    {attr: 'firstName', name: 'Contact', span: 3},
                    {attr: 'title', name: 'Title', span: 3},
                    {attr: 'businessFunction', name: 'Business Function(s)', span: 4},
                    {attr: 'contactPhone', name: 'Phone', span: 3},
                    {attr: 'inactive', name: 'Status', span: 3},
                    {attr: 'sfLastModifiedDate', name: 'Updated', span: 3},
                    {attr: 'notesForCcs', name: 'Notes for CCS', span: 2},
                    {attr: 'edit', name: 'Edit', span: 1, hide: !canEdit},
                    // Two spans, so the header holds on one line at 125% Windows scaling.
                    {attr: 'history', name: 'History', span: 2, hide: !canEdit},
                ]}
                rowsData={rows}
                keyColumn={'guid'}
                cellDisplayFunc={(row, column) => {
                    // Email or phone is required, not both, so a contact can legitimately have no
                    // address. The name then falls back to plain text rather than linking to
                    // mailto:undefined.
                    const email = (row.email ?? '').trim();
                    if (column.attr === 'firstName') {
                        const name = fullName(row);
                        if (name === '') {
                            return EMPTY_CELL;
                        }
                        return email === '' ? name : <EmailLabelWithCopy email={email} label={name}/>;
                    }
                    if (column.attr === 'title') {
                        return orDash(row.title);
                    }
                    if (column.attr === 'businessFunction') {
                        // Salesforce joins the multi-select on a bare semicolon, which gives the
                        // browser nowhere to wrap. Spacing the separators does.
                        return isBlank(row.businessFunction)
                            ? EMPTY_CELL
                            : (row.businessFunction as string).split(';').join('; ');
                    }
                    if (column.attr === 'contactPhone') {
                        return isBlank(row.contactPhone) ? EMPTY_CELL : formatUsPhone(row.contactPhone as string);
                    }
                    if (column.attr === 'inactive') {
                        return <StatusCell row={row}/>;
                    }
                    if (column.attr === 'sfLastModifiedDate') {
                        return formatISODateStr(row.sfLastModifiedDate, 'MM/dd/yyyy hh:mm a');
                    }
                    if (column.attr === 'notesForCcs') {
                        return orDash(row.notesForCcs);
                    }
                    if (column.attr === 'edit') {
                        return <EditContactButton onClick={() => onEdit(row)} contactName={fullName(row)}/>;
                    }
                    if (column.attr === 'history') {
                        return <HistoryContactButton onClick={() => onHistory(row)} contactName={fullName(row)}/>;
                    }
                    return '';
                }}
                emptyMessage={
                    <div className="w-full flex items-center">
                        <div className="m-auto pt-8">
                            {isNarrowed(filter) ? 'No contacts match your search or filter' : 'No contacts yet'}
                        </div>
                    </div>
                }
                loadingFeat={{
                    loading,
                    loadingDisplayElement: <LoadingSpinner spacing={20} height={'30vh'}/>,
                }}
            />
        </Pane>
    );
};
